import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Stack,
  Switch,
  FormControlLabel,
  Collapse,
} from '@mui/material';
import VerificationBadge from '../components/VerificationBadge';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Mail as MailIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  PersonAdd as PersonAddIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { Contact, UserSearchResult, UserDeliverySettings } from '../types';
import { getEmailDomainSync } from '../services/systemConfigService';
import api from '../services/api';

const AddressBook: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Default delivery settings (expanded to match legacy system)
  const [deliverySettings, setDeliverySettings] = useState<UserDeliverySettings>({
    defaultDeliveryFeeRequired: 0,
    defaultMaxPayAmount: 0,
    defaultDaysUntilMailExpires: 90,
    maxDailyLimit: 1000,
    isUnlisted: false,
    defaultCommercialDeliveryFee: 0,
    doNotForwardSMTP: false,
    sendNewClientRequiredMessage: false,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsEdited, setSettingsEdited] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);

  // Form state for add from search
  const [addContactForm, setAddContactForm] = useState({
    displayName: '',
    deliveryFeeRequired: '',
    maxPayAmount: '',
  });

  // Form state for manual add/edit
  const [formData, setFormData] = useState({
    displayName: '',
    firstName: '',
    middleName: '',
    lastName: '',
    address: '',
    deliveryRequired: '0',
    maxPay: '0',
  });

  useEffect(() => {
    loadContacts();
    loadDeliverySettings();
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Contact[]>('/api/contacts');
      setContacts(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const loadDeliverySettings = async () => {
    try {
      const data = await api.get<UserDeliverySettings>('/api/contacts/settings');
      setDeliverySettings(data);
    } catch (err: any) {
      console.error('Failed to load delivery settings:', err);
    }
  };

  const saveDeliverySettings = async () => {
    setSettingsLoading(true);
    try {
      const data = await api.put<UserDeliverySettings>('/api/contacts/settings', {
        defaultDeliveryFeeRequired: deliverySettings.defaultDeliveryFeeRequired,
        defaultMaxPayAmount: deliverySettings.defaultMaxPayAmount,
        defaultDaysUntilMailExpires: deliverySettings.defaultDaysUntilMailExpires,
        maxDailyLimit: deliverySettings.maxDailyLimit,
        isUnlisted: deliverySettings.isUnlisted,
        doNotForwardSMTP: deliverySettings.doNotForwardSMTP,
        sendNewClientRequiredMessage: deliverySettings.sendNewClientRequiredMessage,
      });
      setDeliverySettings(data);
      setSettingsEdited(false);
      setSnackbar({ open: true, message: 'Delivery settings saved', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSearchUsers = useCallback(async () => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const data = await api.get<UserSearchResult[]>('/api/contacts/search', {
        params: { q: searchTerm }
      });
      setSearchResults(data);
    } catch (err: any) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        handleSearchUsers();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, handleSearchUsers]);

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUser(user);
    setAddContactForm({
      displayName: user.fullName || user.username,
      deliveryFeeRequired: deliverySettings.defaultDeliveryFeeRequired.toString(),
      maxPayAmount: deliverySettings.defaultMaxPayAmount.toString(),
    });
  };

  const handleAddFromSearch = async () => {
    if (!selectedUser) return;

    try {
      await api.post('/api/contacts/add-from-search', {
        username: selectedUser.username,
        displayName: addContactForm.displayName || selectedUser.username,
        deliveryFeeRequired: parseFloat(addContactForm.deliveryFeeRequired) || deliverySettings.defaultDeliveryFeeRequired,
        maxPayAmount: parseFloat(addContactForm.maxPayAmount) || deliverySettings.defaultMaxPayAmount,
      });
      setSnackbar({ open: true, message: 'Contact added successfully', severity: 'success' });
      setSearchDialogOpen(false);
      setSelectedUser(null);
      setSearchTerm('');
      setSearchResults([]);
      loadContacts();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to add contact', severity: 'error' });
    }
  };

  const handleOpenDialog = (contact?: Contact) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        displayName: contact.displayName,
        firstName: contact.firstName || '',
        middleName: contact.middleName || '',
        lastName: contact.lastName || '',
        address: contact.address,
        deliveryRequired: contact.deliveryRequired.toString(),
        maxPay: contact.maxPay.toString(),
      });
    } else {
      setEditingContact(null);
      setFormData({
        displayName: '',
        firstName: '',
        middleName: '',
        lastName: '',
        address: '',
        deliveryRequired: deliverySettings.defaultDeliveryFeeRequired.toString(),
        maxPay: deliverySettings.defaultMaxPayAmount.toString(),
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingContact(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.displayName.trim()) {
      setSnackbar({ open: true, message: 'Display name is required', severity: 'error' });
      return;
    }
    if (!formData.address.trim()) {
      setSnackbar({ open: true, message: 'Email address is required', severity: 'error' });
      return;
    }

    const contactData = {
      displayName: formData.displayName.trim(),
      firstName: formData.firstName.trim() || null,
      middleName: formData.middleName.trim() || null,
      lastName: formData.lastName.trim() || null,
      address: formData.address.trim(),
      deliveryRequired: parseFloat(formData.deliveryRequired) || 0,
      maxPay: parseFloat(formData.maxPay) || 0,
    };

    try {
      if (editingContact) {
        await api.put(`/api/contacts/${editingContact.contactID}`, contactData);
        setSnackbar({ open: true, message: 'Contact updated successfully', severity: 'success' });
      } else {
        await api.post('/api/contacts', contactData);
        setSnackbar({ open: true, message: 'Contact created successfully', severity: 'success' });
      }
      handleCloseDialog();
      loadContacts();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to save contact', severity: 'error' });
    }
  };

  const handleDelete = async (contactId: number) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await api.delete(`/api/contacts/${contactId}`);
        setSnackbar({ open: true, message: 'Contact deleted successfully', severity: 'success' });
        loadContacts();
      } catch (err: any) {
        setSnackbar({ open: true, message: err.message || 'Failed to delete contact', severity: 'error' });
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Address Book</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={() => setSearchDialogOpen(true)}
          >
            Find Contact
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Manually
          </Button>
        </Box>
      </Box>

      {/* Default Delivery Settings Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">Default Delivery Settings</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            These defaults are applied when adding new contacts. They can be overridden per contact.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ flex: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Default Postage Required from Unknown Senders"
                type="number"
                value={deliverySettings.defaultDeliveryFeeRequired}
                onChange={(e) => {
                  setDeliverySettings(prev => ({ 
                    ...prev, 
                    defaultDeliveryFeeRequired: parseFloat(e.target.value) || 0 
                  }));
                  setSettingsEdited(true);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Fee required to receive messages from unknown senders"
              />
            </Box>
            <Box sx={{ flex: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Default Maximum Postage You Will Pay"
                type="number"
                value={deliverySettings.defaultMaxPayAmount}
                onChange={(e) => {
                  setDeliverySettings(prev => ({ 
                    ...prev, 
                    defaultMaxPayAmount: parseFloat(e.target.value) || 0 
                  }));
                  setSettingsEdited(true);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Max fee you're willing to pay when sending messages"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={settingsLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={saveDeliverySettings}
                disabled={!settingsEdited || settingsLoading}
              >
                Save
              </Button>
            </Box>
          </Stack>

          {/* Advanced Settings Expandable Section */}
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
              endIcon={showAdvancedSettings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none' }}
            >
              {showAdvancedSettings ? 'Hide' : 'Show'} Advanced Settings
            </Button>
            <Collapse in={showAdvancedSettings}>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Default Days Until Mail Expires"
                      type="number"
                      value={deliverySettings.defaultDaysUntilMailExpires}
                      onChange={(e) => {
                        setDeliverySettings(prev => ({ 
                          ...prev, 
                          defaultDaysUntilMailExpires: parseInt(e.target.value) || 90 
                        }));
                        setSettingsEdited(true);
                      }}
                      inputProps={{ min: 1, max: 365 }}
                      helperText="Number of days until unread messages expire"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Maximum Daily Transaction Limit"
                      type="number"
                      value={deliverySettings.maxDailyLimit}
                      onChange={(e) => {
                        setDeliverySettings(prev => ({ 
                          ...prev, 
                          maxDailyLimit: parseFloat(e.target.value) || 0 
                        }));
                        setSettingsEdited(true);
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 10 }}
                      helperText="Maximum daily transaction limit"
                    />
                  </Box>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deliverySettings.isUnlisted}
                        onChange={(e) => {
                          setDeliverySettings(prev => ({ 
                            ...prev, 
                            isUnlisted: e.target.checked 
                          }));
                          setSettingsEdited(true);
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Hide from User Search</Typography>
                        <Typography variant="caption" color="text.secondary">
                          When enabled, your account won't appear in search results
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deliverySettings.doNotForwardSMTP}
                        onChange={(e) => {
                          setDeliverySettings(prev => ({ 
                            ...prev, 
                            doNotForwardSMTP: e.target.checked 
                          }));
                          setSettingsEdited(true);
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Do Not Forward via SMTP</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Disable forwarding messages to external email
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deliverySettings.sendNewClientRequiredMessage}
                        onChange={(e) => {
                          setDeliverySettings(prev => ({ 
                            ...prev, 
                            sendNewClientRequiredMessage: e.target.checked 
                          }));
                          setSettingsEdited(true);
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Send New Client Required Message</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Notify SMTP senders to use Qixit client
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                </Stack>
              </Box>
            </Collapse>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Contacts Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Display Name</TableCell>
                <TableCell>Qixit Address</TableCell>
                <TableCell align="right">Postage Required From Contact</TableCell>
                <TableCell align="right">Max Postage to Contact</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No contacts yet. Use "Find Contact" to search for Qixit users to add!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact.contactID}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {contact.displayName}
                        </Typography>
                        {(contact.firstName || contact.lastName) && (
                          <Typography variant="caption" color="text.secondary">
                            {[contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(' ')}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<MailIcon />}
                        label={contact.address}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delivery fee you require to receive messages from this contact">
                        <Chip
                          label={`$${contact.deliveryRequired.toFixed(2)}`}
                          size="small"
                          color={contact.deliveryRequired > 0 ? "info" : "default"}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Maximum fee you'll pay when sending to this contact">
                        <Chip
                          label={`$${contact.maxPay.toFixed(2)}`}
                          size="small"
                          color={contact.maxPay > 0 ? "success" : "default"}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(contact)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(contact.contactID)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Search Dialog */}
      <Dialog 
        open={searchDialogOpen} 
        onClose={() => {
          setSearchDialogOpen(false);
          setSelectedUser(null);
          setSearchTerm('');
          setSearchResults([]);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonAddIcon color="primary" />
            Find and Add Contact
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Search for Qixit users by username or name. The contact's required postage is shown to help you set your payment preferences.
          </Typography>
          
          <TextField
            fullWidth
            placeholder="Search by username or name (minimum 2 characters)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              endAdornment: searchLoading ? <CircularProgress size={20} /> : null,
            }}
            sx={{ mb: 2 }}
          />

          {searchResults.length > 0 && !selectedUser && (
            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
              <List dense>
                {searchResults.map((user) => (
                  <ListItemButton 
                    key={user.username}
                    onClick={() => handleSelectUser(user)}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="bold">{user.username}</Typography>
                          {user.accountType && user.accountType !== 'User' && (
                            <Chip label={user.accountType} size="small" color="primary" variant="outlined" />
                          )}
                          <VerificationBadge
                            isEmailVerified={user.isEmailVerified}
                            isPhoneVerified={user.isPhoneVerified}
                            isIdVerified={user.isIdVerified}
                            isAddressVerified={user.isAddressVerified}
                            verificationLevel={user.verificationLevel}
                            size="small"
                          />
                          {!user.isActive && (
                            <Chip label="Inactive" size="small" color="default" />
                          )}
                        </Box>
                      }
                      secondary={user.fullName || 'No name on file'}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="This user's required postage">
                        <Chip
                          label={`$${user.deliveryFeeRequired.toFixed(2)} required`}
                          size="small"
                          color="info"
                        />
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}

          {searchTerm.length >= 2 && searchResults.length === 0 && !searchLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No users found matching "{searchTerm}"
            </Alert>
          )}

          {selectedUser && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add {selectedUser.username} to Contacts
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This user requires <strong>${selectedUser.deliveryFeeRequired.toFixed(2)}</strong> postage to receive messages.
                </Alert>
                
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Display Name (how they appear in your address book)"
                    value={addContactForm.displayName}
                    onChange={(e) => setAddContactForm(prev => ({ ...prev, displayName: e.target.value }))}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Postage You Require From This Contact"
                      type="number"
                      value={addContactForm.deliveryFeeRequired}
                      onChange={(e) => setAddContactForm(prev => ({ ...prev, deliveryFeeRequired: e.target.value }))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Fee to receive messages FROM this contact"
                    />
                    <TextField
                      fullWidth
                      label="Max Postage When Sending TO This Contact"
                      type="number"
                      value={addContactForm.maxPayAmount}
                      onChange={(e) => setAddContactForm(prev => ({ ...prev, maxPayAmount: e.target.value }))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText={`Contact requires $${selectedUser.deliveryFeeRequired.toFixed(2)}`}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSearchDialogOpen(false);
            setSelectedUser(null);
            setSearchTerm('');
            setSearchResults([]);
          }}>
            Cancel
          </Button>
          {selectedUser && (
            <>
              <Button onClick={() => setSelectedUser(null)}>
                Back to Search
              </Button>
              <Button 
                variant="contained" 
                startIcon={<PersonAddIcon />}
                onClick={handleAddFromSearch}
              >
                Add Contact
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Add/Edit Contact Dialog (Manual) */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Contact' : 'Add Contact Manually'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Display Name"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              required
              helperText="How this contact appears in your address book"
            />
            <TextField
              fullWidth
              label="Qixit Address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              placeholder={`username@${getEmailDomainSync()}`}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                label="Middle Name"
                name="middleName"
                value={formData.middleName}
                onChange={handleInputChange}
              />
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </Stack>

            <Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                Payment Settings
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Postage Required From This Contact"
                name="deliveryRequired"
                type="number"
                value={formData.deliveryRequired}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Fee you require to receive from this contact"
              />
              <TextField
                fullWidth
                label="Max Postage Amount"
                name="maxPay"
                type="number"
                value={formData.maxPay}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Max you'll pay to send to this contact"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingContact ? 'Save' : 'Add Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddressBook;
